import type { BoReportService } from '@treasury/domain/backoffice/services/reports';

export const REPORT_DASHBOARD_TAG_NAME = 'tm-bo-report-dashboard';
export const REPORT_VIEW_TAG_NAME = 'tm-bo-report-view';
export const ACH_PAYMENT_DETAIL_TAG_NAME = 'tm-bo-ach-payment-detail';
export const WIRE_PAYMENT_DETAIL_TAG_NAME = 'tm-bo-wire-payment-detail';
export const ACH_FILTER_EXCEPTION_TAG_NAME = 'tm-bo-ach-filter-exception';
export const WIRE_UPLOAD_TAG_NAME = 'tm-bo-wire-upload-report';
export const FI_USER_ACTIVITY_TAG_NAME = 'tm-bo-fi-user-activity';
export const CUSTOMER_USER_ACTIVITY_TAG_NAME = 'tm-bo-customer-user-activity';

export enum ReportType {
    AchPayment = 'achPayment',
    AchNotificationOfChange = 'achNotificationOfChange',
    AchReturn = 'achReturn',
    WireTransferPayment = 'wireTransferPayment',
    WireTransferDliPayment = 'wireTransferDliPayment',
    AchFilterException = 'achFilterException',
    WireUpload = 'wireUpload',
    FiUserActivity = 'boFiUserActivity',
    ChannelUserActivity = 'boChannelUserActivity',
    TransferPaymentReport = 'transferPaymentReport',
    AchFileUpload = 'achFileUpload',
    ArpCheckException = 'arpCheckException',
}

/**
 * Reports whose backend filter endpoint was redesigned to use a cascading
 * filter model. For these report types, report-base.component fetches filters
 * via `getCascadingReportFilters(reportId)` (GET /reports/report/{id}/reportFiltersModel)
 * which returns `GetBoReportFilterModelDto` with `runDefaultSearch` support.
 *
 * All other report types use the older `getReportFilters(type)` endpoint
 * (GET /reports/reportFilter?type={type}).
 *
 * When a new report type adopts the cascading filter model on the backend,
 * add it here so the UI fetches filters from the correct endpoint.
 *
 * NOTE: this list is purely about which GET endpoint to fetch filter
 * definitions from. It has no bearing on the shape of `companies` (or any
 * other field) in the POST /reports request body — legacy Back Office
 * confirms every report, redesigned or not, sends full company objects with
 * no ID-extraction/mapping layer at all.
 */
export const REDESIGNED_REPORTS = [
    ReportType.AchNotificationOfChange,
    ReportType.AchReturn,
    ReportType.TransferPaymentReport,
] as const;

export const DATE_PERIODS: Record<string, string> = {
    Today: 'today',
    SpecificDate: 'specific',
    DateRange: 'range',
    'Week-To-Date': 'week-to-date',
    'Month-To-Date': 'month-to-date',
    'Year-To-Date': 'year-to-date',
};

export const DATE_TYPES = [
    'datePeriod',
    'datePeriodDate',
    'datePeriodEnd',
    'datePeriodStart',
    'issuedDate',
    'createdDate',
    'originalEffectiveDate',
    'effectiveDate',
    'receivedDate',
] as const;

/**
 * Asks the backend directly rather than comparing `reportId` against a
 * hardcoded "base report ID" - those IDs are backend-assigned per FI/
 * environment and aren't stable enough to guess from.
 */
export async function isCustomReport(
    reportService: Pick<BoReportService, 'getReportMetaData'>,
    reportId: string
): Promise<boolean> {
    if (!reportId) {
        return false;
    }
    const metadata = await reportService.getReportMetaData(reportId);
    return metadata?.isCustomReport ?? false;
}

export function isFilterInGetCustomReportFilters<T extends object>(data: T | undefined): data is T {
    return !!data && Object.keys(data).length > 0;
}
